import { useMemo } from 'react';
import type { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { Button, HStack } from '@chakra-ui/react';
import { eachYearOfInterval, getMonth, getYear, parse, startOfYear } from 'date-fns';

import { FIRST_OF_JANUARY_2019 } from '@izimi/modules';

import { CustomIcon } from '../../icon';
import Select from '../../select/Select';

type CustomHeaderProps = {
  formatDate: (date: Date, format: string) => string;
} & ReactDatePickerCustomHeaderProps;

const years = eachYearOfInterval({
  start: startOfYear(FIRST_OF_JANUARY_2019),
  end: new Date(),
}).map((yearDate) => {
  const year = yearDate.getFullYear();
  return { label: year, value: year };
});

export const DatePickerHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  changeMonth,
  changeYear,
  formatDate,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: CustomHeaderProps) => {
  const selectedYear = years.find((year) => year.value === getYear(date)) ?? years[0];

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const monthDate = parse(`${i + 1}`, 'M', new Date());
      return { label: formatDate(monthDate, 'MMMM'), value: i };
    });
  }, [formatDate]);

  return (
    <HStack paddingBottom={3} spacing={1} justify='space-between'>
      <Button variant='link' minW='min-content' isDisabled={prevMonthButtonDisabled}>
        <CustomIcon
          name='chevron-left'
          cursor='pointer'
          onClick={decreaseMonth}
          color='neutral600'
        />
      </Button>
      <Select
        items={months}
        onSelectedItemChange={(item) => item?.value !== undefined && changeMonth(item.value)}
        selectedItem={months[getMonth(date)] ?? null}
        width={32}
        size='sm'
        buttonLabelStyle={{
          fontWeight: '600',
        }}
      />
      <Select
        items={years}
        onSelectedItemChange={(item) => item?.value && changeYear(item.value)}
        selectedItem={selectedYear ?? null}
        size='sm'
        buttonLabelStyle={{
          fontWeight: '600',
        }}
      />
      <Button variant='link' minW='min-content' isDisabled={nextMonthButtonDisabled}>
        <CustomIcon
          name='chevron-right'
          cursor='pointer'
          onClick={increaseMonth}
          color='neutral600'
        />
      </Button>
    </HStack>
  );
};
